import { IMAGE_PRESETS, MISSING_IMAGE_ASSET } from '~/constants/image'
import { isImagePathAbsolute } from '~/utils/image'

import type { GetDynamicImageOptions } from '~/types'

/**
 * This composable provides a function to generate image URLs based on the given
 * path and options.
 */
export default function useImage() {
  const runtimeConfig = useRuntimeConfig()

  const {
    imageWorkerUrlStatic,
    imageWorkerUrlDynamic,
  } = runtimeConfig.public

  /**
   * Returns the base URL for the image service based on the path.
   *
   * @param isStatic Wether to load from the static, or dynamic bucket.
   * @returns The base URL for the image service.
   */
  function getImageWorkerBaseUrl(isStatic?: boolean) {
    return isStatic ? imageWorkerUrlStatic : imageWorkerUrlDynamic
  }

  /**
   * Generates the URL for the CloudFlare image worker.
   *
   * @param options The options for the image.
   * @param options.path The path of the image.
   * @param options.preset The preset to use for the image.
   * @param options.isStatic Whether to load from the static, or dynamic bucket.
   * @param options.withFallback Whether to return a fallback image.
   *
   * @returns The generated CloudFlare image worker URL.
   */
  function getDynamicImage({
    path,
    preset,
    isStatic,
    withFallback,
  }: GetDynamicImageOptions) {
    let resolvedPath = path
    let resolvedIsStatic = isStatic

    if (!resolvedPath && withFallback) {
      resolvedPath = MISSING_IMAGE_ASSET
      resolvedIsStatic = true
    }

    if (!resolvedPath) {
      return
    }

    if (isImagePathAbsolute(resolvedPath)) {
      return resolvedPath
    }

    if (!resolvedPath.startsWith('/')) {
      resolvedPath = `/${resolvedPath}`
    }

    const url = new URL(`${getImageWorkerBaseUrl(resolvedIsStatic)}${resolvedPath}`)

    if (preset) {
      const presetKey = typeof preset === 'string'
        ? IMAGE_PRESETS[preset].key
        : preset.key

      url.searchParams.set('preset', presetKey)
    }

    return url.toString()
  }

  /**
   * Thin wrapper around getDynamicImage to set isStatic to true.
   *
   * @param options The options for the image.
   * @param options.path The path of the image.
   * @param options.preset The preset to use for the image.
   *
   * @returns The generated CloudFlare image worker URL.
   */
  function getStaticImage(options: Omit<GetDynamicImageOptions, 'isStatic'>) {
    return getDynamicImage({ ...options, isStatic: true })
  }

  return {
    getDynamicImage,
    getStaticImage,
    presets: IMAGE_PRESETS,
  }
}
